import React from "react";

/**
 * Pill tabs for the Bookings page.
 *
 * Same accessibility contract as Broadcasts' underline `Tabs` — tablist role,
 * aria-selected, roving tabIndex, arrow-key navigation with wraparound — but a
 * grey filled pill instead of an orange underline, which is what this screen's
 * design calls for. Kept local rather than importing across page folders (a
 * pattern this repo doesn't use yet); promoting Tabs into `common/comms` with a
 * `variant` prop would be the tidier long-term move.
 *
 * @param {Object} props
 * @param {string} props.value - Id of the selected tab.
 * @param {(id: string) => void} props.onChange - Called with the newly selected id.
 * @param {Array<{ id: string, label: React.ReactNode, count?: number|null }>} props.items - Tabs, in order.
 * @param {string} props.label - Accessible name for the tablist.
 * @param {string} [props.idPrefix='bk-tab'] - Prefix for the generated tab ids.
 */
export const BookingsTabs = ({
  value,
  onChange,
  items = [],
  label,
  idPrefix = "bk-tab",
}) => {
  /** ArrowLeft/ArrowRight move between tabs and wrap at both ends. */
  const handleKeyDown = (event, index) => {
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0 || items.length === 0) return;
    event.preventDefault();
    const next = items[(index + step + items.length) % items.length];
    if (next) onChange?.(next.id);
  };

  return (
    <div role="tablist" aria-label={label} className="flex items-center gap-1">
      {items.map((item, index) => {
        const isActive = item.id === value;
        // Same convention as Broadcasts' `Tabs`: a count of 0 is a result and
        // prints, a missing count prints nothing at all — a fabricated 0 reads
        // as "we counted, and found none", which is a different claim from
        // "we haven't counted yet".
        const hasCount = item.count !== null && item.count !== undefined;

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${idPrefix}-${item.id}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-${item.id}-panel`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange?.(item.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1 ${
              isActive
                ? "bg-light-gray font-semibold text-orange-primary"
                : "text-bc-text-2 hover:bg-light-gray/60"
            }`}
          >
            <span>{item.label}</span>
            {hasCount ? (
              // The pill inverts on the active tab: a grey pill on the active
              // tab's grey fill would disappear into it.
              <span
                className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
                  isActive ? "bg-white text-orange-primary" : "bg-light-gray text-bc-soft"
                }`}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
